// Client REAL de Excepciones. Es el BORDE con el silo: acá y solo acá se traduce su vocabulario.
//
// ✅ Los siete endpoints existen, están desplegados y verificados en runtime contra `fiado-dev`.
//
// 🔴 El path usa `{exceptionId}`, NUNCA `{id}`: API Gateway no admite dos variables con nombre
// distinto como hermanas en el mismo nivel, y los hermanos ya desplegados usan `{exceptionId}`.
//
// 🔴 POR QUÉ HAY UN MAPPER ACÁ
// Antes no lo había: `apiFetch` devolvía `wrapper.data` y un `as ExceptionsResponse` de TypeScript
// fingía que el shape coincidía. No coincidía en diez campos, y el cast no falla en tiempo de
// compilación ni de ejecución — solo hace que la pantalla pinte `undefined`. La normalización vive
// en el borde para que ningún componente tenga que saber cómo se llaman las cosas del otro lado.
import { apiFetch } from '../../../client';
import type {
    CreateExceptionRequest,
    ExceptionCountsByStatus,
    ExceptionDetail,
    ExceptionDetailWire,
    ExceptionStatusCounts,
    ExceptionsApi,
    ExceptionsResponse,
    ExceptionsResponseWire,
    ResolveExceptionRequest,
} from './exceptionsContract';

const BASE_URL = import.meta.env.VITE_API_RETAIL_WIZARD_BASE_URL;

/**
 * Los conteos del silo llegan keyed por el enum y **sin total**: la pill «Todas» leía `counts.all`,
 * que no existía, y por eso los seis contadores decían «—» aunque el backend los calculara bien.
 *
 * El total se suma acá y no se toma de `items.length`: la lista puede venir topada (el repo corta
 * en 500) y entonces el total sería menor que la realidad sin que nada avise.
 */
function normalizeCounts(byStatus: ExceptionCountsByStatus | undefined): ExceptionStatusCounts {
    const source = byStatus ?? {};
    const counts = {
        NEW: source.NEW ?? 0,
        IN_REVIEW: source.IN_REVIEW ?? 0,
        APPROVED: source.APPROVED ?? 0,
        APPLIED: source.APPLIED ?? 0,
        REJECTED: source.REJECTED ?? 0,
    };
    // Se suman los VALORES del objeto crudo, no los cinco normalizados: si el silo devolviera un
    // estado que este front no conoce (pasó con una fila corrupta en dev, `status: "ZOMBIE"`), su
    // conteo entraría igual al total y la suma seguiría cuadrando con el número de filas.
    const all = Object.values(source).reduce<number>((sum, n) => sum + n, 0);
    return { ...counts, all };
}

/** Las cuatro acciones del ciclo comparten forma: POST a un sub-recurso del caso. */
const action = (exceptionId: string, verb: string, body: unknown) =>
    apiFetch<ExceptionDetailWire>(
        `/backoffice/exceptions/${encodeURIComponent(exceptionId)}/${verb}`,
        {
            method: 'POST',
            body: JSON.stringify(body),
            baseUrl: BASE_URL,
        },
    );

export const exceptionsClient: ExceptionsApi = {
    listExceptions: async (): Promise<ExceptionsResponse> => {
        const wire = await apiFetch<ExceptionsResponseWire>('/backoffice/exceptions', {
            baseUrl: BASE_URL,
        });
        return {
            items: wire.items,
            counts: normalizeCounts(wire.countsByStatus),
        };
    },
    getException: (exceptionId: string): Promise<ExceptionDetail> =>
        apiFetch<ExceptionDetailWire>(`/backoffice/exceptions/${encodeURIComponent(exceptionId)}`, {
            baseUrl: BASE_URL,
        }),
    // Las claves opcionales se OMITEN cuando no hay valor, no viajan como `null`: los DTO del silo
    // las declaran opcionales y un `null` explícito falla la validación de `class-validator`.
    createException: (request: CreateExceptionRequest): Promise<ExceptionDetail> =>
        apiFetch<ExceptionDetailWire>('/backoffice/exceptions', {
            method: 'POST',
            body: JSON.stringify(request),
            baseUrl: BASE_URL,
        }),
    resolveException: (exceptionId: string, request: ResolveExceptionRequest) =>
        action(exceptionId, 'resolve', request),
    reviewException: (exceptionId: string, note?: string) =>
        action(exceptionId, 'review', note === undefined ? {} : { note }),
    applyException: (exceptionId: string, note?: string) =>
        action(exceptionId, 'apply', note === undefined ? {} : { note }),
    addExceptionNote: (exceptionId: string, body: string) => action(exceptionId, 'notes', { body }),
};
